import React, { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckIcon, PencilIcon, PlayIcon, Trash2Icon } from 'lucide-react';
import { HeaderStage } from '../components/HeaderStage';
import { TrackTable } from '../components/TrackTable';
import { useLibrary } from '../contexts/LibraryContext';
import { usePlayer } from '../contexts/PlayerContext';
import { usePageMeta } from '../hooks/usePageMeta';
export function Playlist() {
  const {
    id = ''
  } = useParams();
  const navigate = useNavigate();
  const {
    playlists,
    renamePlaylist,
    deletePlaylist,
    removeFromPlaylist,
    updateNote
  } = useLibrary();
  const {
    play
  } = usePlayer();
  const playlist = playlists.find(p => p.id === id);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(playlist?.name ?? '');
  usePageMeta({
    title: playlist ? `${playlist.name} · Playlist` : 'Playlist not found',
    description: playlist ? `${playlist.name} — ${playlist.tracks.length} ${playlist.tracks.length === 1 ? 'track' : 'tracks'} on Noctuzan${playlist.note ? `. ${playlist.note}` : ''}. Real previews from the iTunes catalogue, saved in this browser.` : 'This Noctuzan set is no longer saved in this browser.',
    image: playlist?.tracks[0]?.artwork,
    path: `/playlist/${id}`
  });
  if (!playlist) {
    return <div className="px-5 pb-16 pt-8 sm:px-8 lg:px-12" data-id="element-386" data-mp="6s5hflvvkhp9">
        <h1 className="font-display text-3xl text-cream" data-id="element-387" data-mp="6qkhfgqsk6k0">That set is gone</h1>
        <p className="mt-2 text-sm text-muted" data-id="element-388" data-mp="br8a06uo4vgc">It may have been deleted from this browser.</p>
        <Link to="/library" className="mt-6 inline-block rounded-full border border-ink-line px-5 py-2.5 text-sm text-cream transition-colors duration-150 ease-night hover:border-gold/60 hover:text-gold focus:outline-none focus-visible:ring-2 focus-visible:ring-iris-soft" data-id="element-389" data-mp="3twv0xjdeog02">
          Back to library
        </Link>
      </div>;
  }
  const covers = playlist.tracks.slice(0, 4);
  const commitName = () => {
    renamePlaylist(playlist.id, draft);
    setEditing(false);
  };
  return <div className="relative px-5 pb-16 pt-8 sm:px-8 lg:px-12" data-id="element-390" data-mp="o2rdztf8iie0">
      <HeaderStage artwork={covers[0]?.artwork} data-id="element-391" data-mp="ja7rg6ykkcx2" />
      <motion.div initial={{
      opacity: 0,
      y: 12
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.26,
      ease: [0.23, 1, 0.32, 1]
    }} className="relative z-10 flex flex-col gap-6 border-b border-ink-line pb-10 sm:flex-row sm:items-end" data-id="element-392" data-mp="j8wp8bkrvd82">
        <div className="grid h-40 w-40 flex-none grid-cols-2 grid-rows-2 overflow-hidden rounded-md bg-ink-raised shadow-2xl shadow-black/50 ring-1 ring-cream/10" data-id="element-393" data-mp="5a6qysm7v7i4">
          {covers.length === 0 && <span className="col-span-2 row-span-2" data-id="element-394" data-mp="3shscapipuemn" />}
          {covers.map((track, i) => <img key={track.id} src={track.artworkSmall} alt="" className={`h-full w-full object-cover ${covers.length === 1 ? 'col-span-2 row-span-2' : ''}`} style={{
          gridColumn: covers.length === 1 ? 'span 2' : undefined
        }} loading={i > 1 ? 'lazy' : undefined} data-id="element-395" data-mp="1upwe0x7wv9xc" data-mpi={i} />)}
        </div>

        <div className="min-w-0 flex-1" data-id="element-396" data-mp="3idbzy64g3fnl">
          <p className="text-sm text-muted" data-id="element-397" data-mp="3ien0lx2lg7ah">Playlist · saved on this device</p>
          {editing ? <div className="mt-2 flex items-center gap-2" data-id="element-398" data-mp="3mfp10h88ype1">
              <input autoFocus value={draft} onChange={e => setDraft(e.target.value)} onKeyDown={e => e.key === 'Enter' && commitName()} aria-label="Playlist name" className="w-full max-w-md rounded border border-ink-line bg-ink-panel px-3 py-2 font-display text-2xl text-cream focus:border-gold/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-iris-soft" data-id="element-399" data-mp="2powsq5rk21mt" />
              <button type="button" onClick={commitName} aria-label="Save playlist name" className="rounded-full bg-gold p-2 text-ink-base focus:outline-none focus-visible:ring-2 focus-visible:ring-iris-soft" data-id="element-400" data-mp="iw488466iffr">
                <CheckIcon className="h-4 w-4" aria-hidden="true" data-id="element-401" data-mp="qgwwbjkp0h4h" />
              </button>
            </div> : <h1 className="mt-2 flex items-center gap-3 font-display text-4xl leading-tight text-cream" data-id="element-402" data-mp="me9xgypltvxs">
              <span className="truncate" data-id="element-403" data-mp="10d9qeik5gwzr">{playlist.name}</span>
              <button type="button" onClick={() => {
            setDraft(playlist.name);
            setEditing(true);
          }} aria-label="Rename playlist" className="flex-none rounded-full p-1.5 text-muted transition-colors duration-150 ease-night hover:text-gold focus:outline-none focus-visible:ring-2 focus-visible:ring-iris-soft" data-id="element-404" data-mp="vgjapwc9c3ev">
                <PencilIcon className="h-4 w-4" aria-hidden="true" data-id="element-405" data-mp="1e7tw5xz7rnzd" />
              </button>
            </h1>}

          <input value={playlist.note} onChange={e => updateNote(playlist.id, e.target.value)} placeholder="Add a liner note…" aria-label="Playlist note" className="mt-3 w-full max-w-lg rounded border border-transparent bg-transparent px-0 py-1 text-sm italic text-muted placeholder:text-muted/70 focus:border-b-gold/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-iris-soft" data-id="element-406" data-mp="1xpybgxb1a74t" />

          <div className="mt-5 flex items-center gap-3" data-id="element-407" data-mp="1jt1iap7g0dkr">
            <button type="button" onClick={() => play(playlist.tracks, 0)} disabled={playlist.tracks.length === 0} className="flex items-center gap-2 rounded-full bg-gold px-5 py-2.5 text-sm font-medium text-ink-base transition-transform duration-150 ease-night hover:scale-[1.02] disabled:opacity-40 disabled:hover:scale-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-iris-soft focus-visible:ring-offset-2 focus-visible:ring-offset-ink-base" data-id="element-408" data-mp="153nahljim47h">
              <PlayIcon className="h-4 w-4" aria-hidden="true" data-id="element-409" data-mp="2xz6aoo5k66zl" />
              Play set
            </button>
            <span className="text-xs text-muted" data-id="element-410" data-mp="2t1xtaps8i6ze">
              {playlist.tracks.length} {playlist.tracks.length === 1 ? 'track' : 'tracks'}
            </span>
            <button type="button" onClick={() => {
            deletePlaylist(playlist.id);
            navigate('/library');
          }} className="ml-auto flex items-center gap-1.5 rounded-full border border-ink-line px-3.5 py-2 text-xs text-muted transition-colors duration-150 ease-night hover:border-iris-soft/60 hover:text-iris-soft focus:outline-none focus-visible:ring-2 focus-visible:ring-iris-soft" data-id="element-411" data-mp="3lpjszmyccrgz">
              <Trash2Icon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-412" data-mp="217ly8dh7ed6h" />
              Delete
            </button>
          </div>
        </div>
      </motion.div>

      {playlist.tracks.length === 0 ? <p className="relative z-10 mt-10 max-w-md text-sm leading-relaxed text-muted" data-id="element-413" data-mp="br060cd8mgnm">
          Empty for now. Use the <span className="text-cream" data-id="element-414" data-mp="ktj30fucdz1y">＋</span> beside any track in Home or Search to drop it
          in here.
        </p> : <div className="relative z-10 mt-8 overflow-x-auto scroll-thin" data-id="element-415" data-mp="pl9jl3jvhj1t">
          <TrackTable tracks={playlist.tracks} onRemove={track => removeFromPlaylist(playlist.id, track.id)} data-id="element-416" data-mp="ujkrh8qlxjhe" />
        </div>}
    </div>;
}